import React from 'react';
import { TabType } from '../screens/DashboardScreen';
import { UrgentConsulting } from '../types/api';
import { 
  useNeedPatientMeeting, 
  useMakeConferenceCall, 
  useConferenceCallDone 
} from '../hooks/useUrgentConsulting';

interface ConsultationCardProps {
  consultation: UrgentConsulting;
  tabType: TabType;
}

export const ConsultationCard: React.FC<ConsultationCardProps> = ({
  consultation,
  tabType,
}) => {
  const needPatientMeetingMutation = useNeedPatientMeeting();
  const makeConferenceCallMutation = useMakeConferenceCall();
  const conferenceCallDoneMutation = useConferenceCallDone();

  const formatTimeAgo = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  const getPriorityColor = (urgentConsulting: UrgentConsulting) => {
    // Determine priority based on business logic
    const isUrgent = urgentConsulting.need_patient_meeting || urgentConsulting.status === 'in_progress';
    const isOld = new Date().getTime() - new Date(urgentConsulting.created_at).getTime() > 2 * 60 * 60 * 1000; // 2 hours
    
    if (isUrgent || isOld) {
      return 'bg-red-100 text-red-800 border-red-200';
    }
    return 'bg-yellow-100 text-yellow-800 border-yellow-200';
  };

  const getStatusInfo = (urgentConsulting: UrgentConsulting) => {
    switch (urgentConsulting.status) {
      case 'created':
        return { color: 'bg-blue-100 text-blue-800', text: 'New' };
      case 'in_progress':
        return { color: 'bg-orange-100 text-orange-800', text: 'In Progress' };
      case 'done':
        return { color: 'bg-statusBg text-statusText', text: 'Completed' };
      default:
        return { color: 'bg-gray-100 text-gray-800', text: urgentConsulting.status };
    }
  };

  const statusInfo = getStatusInfo(consultation);

  const handleNeedPatientMeeting = async () => {
    try {
      await needPatientMeetingMutation.mutateAsync(consultation.id);
    } catch (error) {
      console.error('Failed to request patient meeting:', error);
    }
  };

  const handleStartCall = async () => {
    try {
      await makeConferenceCallMutation.mutateAsync(consultation.id);
    } catch (error) {
      console.error('Failed to start conference call:', error);
    }
  };

  const handleCallDone = async () => {
    try {
      await conferenceCallDoneMutation.mutateAsync(consultation.id);
    } catch (error) {
      console.error('Failed to complete call:', error);
    }
  };

  return (
    <div className="bg-white rounded-xl p-6 shadow-sm border border-borderBottomColor hover:shadow-md transition-all duration-200">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        {/* Main Content */}
        <div className="flex-1">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 mb-3">
            {tabType === 'open' || tabType === 'needPatientMeeting' || tabType === 'done' ? (
              <h3 className="text-lg font-semibold text-blackText">
                HM ID: {consultation.HM_ID || `HM-URC-${consultation.id}`}
              </h3>
            ) : (
              <>
                <h3 className="text-lg font-semibold text-blackText">
                  {consultation.patient_name || 'Unknown Patient'}
                </h3>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getPriorityColor(consultation)}`}>
                    {consultation.need_patient_meeting ? 'HIGH' : 'MEDIUM'} PRIORITY
                  </span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusInfo.color}`}>
                    {statusInfo.text}
                  </span>
                </div>
              </>
            )}
          </div>
          
          <div className="mb-3 space-y-1">
            <p className="text-grayText text-sm">
              <strong>Therapist:</strong> {consultation.therapist_first_name} {consultation.therapist_last_name}
            </p>
            {consultation.email && (
              <p className="text-grayText text-sm">
                <strong>Email:</strong> {consultation.email}
              </p>
            )}
            {(tabType === 'needPatientMeeting' || tabType === 'done') && (
              <p className="text-grayText text-sm">
                <strong>Patient:</strong> {consultation.patient_name || 'Unknown Patient'}
              </p>
            )}
            {consultation.organisation && (
              <p className="text-grayText text-sm">
                <strong>Organisation:</strong> {consultation.organisation}
              </p>
            )}
          </div>
          
          <div className="flex flex-col xs:flex-row xs:items-center gap-2 xs:gap-4 text-sm text-grayText">
            <div className="flex items-center gap-1">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>{formatTimeAgo(consultation.created_at)}</span>
            </div>
            
            {consultation.phone && (
              <div className="flex items-center gap-1">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                <span>{consultation.phone}</span>
              </div>
            )}
          </div>
          
          {consultation.comments && (
            <div className="mt-3 p-3 bg-paleGreen rounded-lg border border-borderPaleGreen">
              <p className="text-sm text-blackText">
                <strong>Comments:</strong> {consultation.comments}
              </p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-row sm:flex-col gap-2 sm:min-w-[120px]">
          {tabType === 'open' && (
            <>
              <button
                onClick={handleStartCall}
                disabled={makeConferenceCallMutation.isPending}
                className="flex-1 sm:flex-none px-4 py-2 bg-darkGreen text-white rounded-lg hover:bg-opacity-90 transition-colors text-sm font-medium disabled:opacity-50"
              >
                {makeConferenceCallMutation.isPending ? 'Starting...' : 'Start Call'}
              </button>
              <button
                onClick={handleNeedPatientMeeting}
                disabled={needPatientMeetingMutation.isPending}
                className={`flex-1 sm:flex-none px-4 py-2 rounded-lg transition-colors text-sm font-medium disabled:opacity-50 ${
                  consultation.need_patient_meeting 
                    ? 'bg-gray-300 text-gray-600 hover:bg-gray-400' 
                    : 'bg-primaryBg text-darkGreen hover:bg-opacity-80'
                }`}
              >
                {needPatientMeetingMutation.isPending 
                  ? 'Requesting...' 
                  : consultation.need_patient_meeting 
                    ? 'Meeting Requested' 
                    : 'Need Meeting'
                }
              </button>
              <button
                onClick={handleCallDone}
                disabled={conferenceCallDoneMutation.isPending}
                className="flex-1 sm:flex-none px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors text-sm font-medium disabled:opacity-50"
              >
                {conferenceCallDoneMutation.isPending ? 'Finishing...' : 'Finish Meeting'}
              </button>
            </>
          )}
          
          {tabType === 'needPatientMeeting' && (
            <div className="flex-1 sm:flex-none px-4 py-2 bg-yellowBg text-blackText rounded-lg text-sm font-medium text-center">
              Status: {consultation.patient_meeting_status.replace('_', ' ')}
            </div>
          )}
          
          {tabType === 'done' && (
            <div className="flex-1 sm:flex-none px-4 py-2 bg-gray-100 text-gray-600 rounded-lg text-sm font-medium text-center">
              Completed
            </div>
          )}
        </div>
      </div>
    </div>
  );
};